import Phaser from 'phaser'
import { useEffect, useRef } from 'react'

import { OfficeScene, type OfficeSceneData } from '~/game/scenes/OfficeScene'
import type { WorldSnapshot } from '~/data/types'
import type { EventBus } from '~/realtime/bus'

interface Props {
  snapshot: WorldSnapshot
  bus: EventBus
  onFocus: (userId: string | null) => void
  onRoomFocus?: (pulseId: string | null) => void
  onHire?: (pulseId: string) => void
  onOfficeFocus?: (officeId: string) => void
  onTasksFocus?: (pulseId: string) => void
  onNotesFocus?: (pulseId: string) => void
  onHoverPerson?: (userId: string | null, x: number, y: number) => void
  canHire?: boolean
  arrangeMode?: boolean
  onRoomMoved?: (roomId: string, dx: number, dy: number) => void
  /** Bumped when saved room offsets change — forces a re-layout restart. */
  layoutNonce?: number
  selfUserId?: string | null
}

/** Mounts a Phaser game filling its parent and hands the snapshot to the scene. */
export function PhaserGame({
  snapshot,
  bus,
  onFocus,
  onRoomFocus,
  onHire,
  onOfficeFocus,
  onTasksFocus,
  onNotesFocus,
  onHoverPerson,
  canHire,
  arrangeMode,
  onRoomMoved,
  layoutNonce,
  selfUserId,
}: Props) {
  const parentRef = useRef<HTMLDivElement>(null)
  const gameRef = useRef<Phaser.Game>()

  useEffect(() => {
    if (!parentRef.current) return

    const game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: parentRef.current,
      backgroundColor: '#0e0f14',
      pixelArt: true,
      scale: { mode: Phaser.Scale.RESIZE, width: '100%', height: '100%' },
    })
    gameRef.current = game

    const data: OfficeSceneData = {
      snapshot,
      bus,
      onFocus,
      onRoomFocus,
      onHire,
      onOfficeFocus,
      onTasksFocus,
      onNotesFocus,
      onHoverPerson,
      canHire,
      arrangeMode,
      onRoomMoved,
      selfUserId: selfUserId ?? undefined,
    }
    game.scene.add('office', OfficeScene, true, data)

    return () => {
      game.destroy(true)
      gameRef.current = undefined
    }
    // Bus is stable for the stage's lifetime; mount once. A changed snapshot is
    // applied via scene.restart below (so meeting realtime updates don't reload).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When the snapshot reference changes (e.g. a meeting went live/ended and we
  // refetched meetings), restart the office scene with the new data instead of
  // reloading the page. computeLayout is pure, so this re-resolves the floor plan
  // deterministically; the same EventBus stays subscribed.
  useEffect(() => {
    const game = gameRef.current
    if (!game) return
    const office = game.scene.getScene('office')
    if (!office) return
    const data: OfficeSceneData = {
      snapshot,
      bus,
      onFocus,
      onRoomFocus,
      onHire,
      onOfficeFocus,
      onTasksFocus,
      onNotesFocus,
      onHoverPerson,
      canHire,
      arrangeMode,
      onRoomMoved,
      selfUserId: selfUserId ?? undefined,
    }
    office.scene.restart(data)
  }, [
    snapshot,
    bus,
    onFocus,
    onRoomFocus,
    onHire,
    onOfficeFocus,
    onTasksFocus,
    onNotesFocus,
    onHoverPerson,
    canHire,
    arrangeMode,
    onRoomMoved,
    layoutNonce,
    selfUserId,
  ])

  return <div ref={parentRef} style={{ position: 'absolute', inset: 0 }} />
}
